import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DashboardAnimation from './DashboardAnimation';

const Hero = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (sectionId) => (e) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      // If we're on the home page, scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If we're on another page, navigate to home page with hash
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <section className="pt-28 pb-20 overflow-hidden bg-gradient-to-b from-white to-purple-50">
      <div className="container mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            <h1 className="mb-6 leading-tight">
              Complete <span className="text-gradient">Observability</span> for Your Data Platform
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-xl mx-auto lg:mx-0">
              Discover, monitor, and ensure the quality of your data across all sources with 
              AI-powered insights and advanced lineage tracking.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button onClick={handleScrollToSection('request-demo')} className="button button-primary">
                Request a Demo
              </button>
              <button onClick={handleScrollToSection('features')} className="button button-secondary">
                Explore Features
              </button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <DashboardAnimation />
            
            {/* Floating elements */}
            <motion.div 
              className="absolute -bottom-6 -left-6 w-20 h-20 bg-purple-200 rounded-lg z-[-1]"
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, 0]
              }}
              transition={{ 
                repeat: Infinity,
                duration: 5,
                ease: "easeInOut"
              }}
            ></motion.div>
            
            <motion.div 
              className="absolute -top-8 -right-6 w-16 h-16 bg-teal-200 rounded-full z-[-1]"
              animate={{ 
                y: [0, 10, 0],
                x: [0, 10, 0]
              }}
              transition={{ 
                repeat: Infinity,
                duration: 6,
                ease: "easeInOut"
              }}
            ></motion.div>
          </motion.div>
        </div>
        
        {/* Highlight markers */}
        { <div className="mt-16 text-center">
          <h3 className="text-lg text-gray-600 mb-8">Trusted by data teams at</h3>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-8 items-center justify-items-center">
            {[
              // { 
              //   name: 'Bravo Telecom', 
              //   logo: 'https://www.bravotelecom.com/wp-content/uploads/2022/11/bravotelecom-logo3-300x105.png'
              // },
              { 
                name: 'INWI', 
                logo: 'https://tse2.mm.bing.net/th?id=OIP.LUtMqIwzBlr8E1GmsyX8-AHaFW&pid=Api'
              },
              // { 
              //   name: 'Toguna', 
              //   logo: 'https://cdn.prod.website-files.com/6528ffe2fbfa0c0ffc42f4b7/6528ffe2fbfa0c0ffc42f5f7_logo%20toguna%20small.png'
              // }
            ].map((company, i) => (
              <motion.div
                key={company.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className="flex flex-col items-center"
              >
                <div className="h-16 flex items-center justify-center mb-2">
                  <img 
                    src={company.logo} 
                    alt={`${company.name} logo`} 
                    className="max-h-14 max-w-full transition-all duration-300 filter grayscale hover:grayscale-0 hover:opacity-100 opacity-70"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-gray-500 font-medium hidden">{company.name}</span>
                </div>
                <span className="text-sm text-gray-500">{company.name}</span>
              </motion.div>
            ))}
          </div>
        </div> }
      </div>
      
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
      <div className="absolute top-0 right-96 w-72 h-72 bg-teal-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
    </section>
  );
};

export default Hero; 